<template>
  <div>
    <v-app-bar flat color="transparent" class="main-appbar">
      <app-bar-nav-icon />
      <v-skeleton-loader
        type="chip"
        transition="fade-transition"
        v-if="loading"
      />

      <v-toolbar-title v-else class="headline font-weight-bold pa-0">
        Resources ({{ listMeta.total }})
      </v-toolbar-title>

      <v-spacer />

      <v-row v-show="showSearch" align="center" style="max-width: 300px;">
        <v-text-field
          class="search"
          ref="searchField"
          flat
          hide-details
          solo
          rounded
          clearable
          background-color="grey lighten-3"
          label="Search title, safety tips, instructions, or attachments"
          v-model="filter.search"
          @input="search"
        />
      </v-row>

      <v-skeleton-loader
        type="actions"
        transition="fade-transition"
        v-if="loading"
      />

      <template v-else>
        <v-btn icon class="ml-5" @click="showSearchField">
          <img src="@/assets/icons/search.svg" alt height="17.7px" />
        </v-btn>

        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              color="#383d3f"
              class="mr-1"
              icon
              @click="exportList"
              v-on="on"
            >
              <v-icon>{{ icons.export }}</v-icon>
            </v-btn>
          </template>
          <span>Export to Excel</span>
        </v-tooltip>
      </template>
    </v-app-bar>

    <div class="d-flex mt-6">
      <div>
        <v-btn
          color="primary"
          @click="
            $router.push({
              name: 'milestone.resources.new'
            })
          "
        >
          <v-icon class="mr-1" small>{{ icons.add }}</v-icon>
          Add new resource
        </v-btn>
      </div>

      <v-spacer></v-spacer>

      <div class="d-flex">
        <v-select
          class="ml-10"
          hide-details
          flat
          solo
          dense
          item-text="label"
          item-value="value"
          v-model="defaultFilter"
          :items="filterBy"
          style="max-width: 200px;"
          @change="filterResource"
        />

        <v-btn
          icon
          class="ml-3"
          :color="sortOrder ? 'primary' : ''"
          @click="
            sortOrder = true
            filterResource()
          "
        >
          <v-icon>{{ icons.arrowUp }}</v-icon>
        </v-btn>

        <v-btn
          icon
          :color="!sortOrder ? 'primary' : ''"
          @click="
            sortOrder = false
            filterResource()
          "
        >
          <v-icon>{{ icons.arrowDown }}</v-icon>
        </v-btn>
      </div>
    </div>

    <div
      class="mt-6"
      id="resource-list"
      v-infinite-scroll="loadMore"
      :infinite-scroll-disabled="loading"
      infinite-scroll-distance="100"
    >
      <v-simple-table dense>
        <thead>
          <tr>
            <th width="20%" class="text-left">Title</th>
            <th width="20%" class="text-left">Milestone</th>
            <th width="20%" class="text-left">Safety Tips</th>
            <th width="20%" class="text-left">Attachments</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="resource in resources" :key="resource.id">
            <td width="20%" class="clickable" @click="showDetails(resource.id)">
              {{ resource.title }}
            </td>
            <td width="20%" class="clickable" @click="showDetails(resource.id)">
              {{ resource.milestone.name }}
            </td>
            <td width="20%" class="clickable" @click="showDetails(resource.id)">
              {{ resource.safety_tips }}
            </td>
            <td width="20%">
              <ul>
                <li
                  v-for="attachment in resource.attachments"
                  :key="attachment.id"
                >
                  <a :href="attachment.url" download>
                    {{ attachment.file_name }}
                  </a>
                </li>
              </ul>
            </td>
          </tr>

          <template v-if="loading">
            <tr v-for="index in 10" :key="index + '-skeleton'">
              <td v-for="i in 4" :key="i" class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-simple-table>

      <div
        class="d-flex justify-center mt-3"
        v-if="!(resources.length > 0) && !loading"
      >
        <p>No match found.</p>
      </div>
    </div>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" right>
      {{ snackbar.message }}
      <v-btn color="white" text @click="snackbar.show = false">Ok</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import {
  mdiDotsVertical,
  mdiUnfoldMoreHorizontal,
  mdiMagnify,
  mdiAccountCircleOutline,
  mdiChevronRight,
  mdiFileExcel,
  mdiArrowUp,
  mdiArrowDown,
  mdiPlus
} from '@mdi/js'
import ControlsMixin from '@/utils/mixins/Controls'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import infiniteScroll from 'vue-infinite-scroll'
import debounce from 'lodash/debounce'

export default {
  name: 'ResourcesPage',

  mixins: [ControlsMixin],

  directives: {
    infiniteScroll
  },

  components: {
    AppBarNavIcon
  },

  data() {
    return {
      loading: false,
      showSearch: false,
      filter: {
        search: ''
      },
      icons: {
        settings: mdiDotsVertical,
        sort: mdiUnfoldMoreHorizontal,
        search: mdiMagnify,
        view: mdiAccountCircleOutline,
        arrow: mdiChevronRight,
        export: mdiFileExcel,
        arrowUp: mdiArrowUp,
        arrowDown: mdiArrowDown,
        add: mdiPlus
      },
      snackbar: {
        show: false,
        message: null,
        color: null
      },
      defaultFilter: 'title',
      sortOrder: true,
      filterBy: [
        {
          value: 'title',
          label: 'Title'
        },
        {
          value: 'safety_tips',
          label: 'Safety Tips'
        },
        {
          value: 'instructions',
          label: 'Instructions'
        }
      ]
    }
  },

  created() {
    this.clearResources()
    this.fetchResources(1)
  },

  updated() {
    this.$nextTick(() => {
      setTimeout(() => {
        this.$refs.searchField.$refs.input.focus()
      })
    })
  },

  computed: {
    ...mapState({
      resources: state => state.resource.list,
      listMeta: state => state.resource.listMeta
    }),

    paginationLength() {
      return (this.listMeta.total / this.listMeta.per_page) | 0
    }
  },

  methods: {
    ...mapActions({
      getResources: 'resource/getResources',
      exportToExcel: 'resource/exportToExcel'
    }),

    ...mapMutations({
      clearResources: 'resource/clearResourceList'
    }),

    pageChanged(page) {
      this.fetchResources(page)
    },

    filterResource(filterValue) {
      this.clearResources()
      this.fetchResources(null, filterValue)
    },

    loadMore() {
      if (this.listMeta.current_page < this.listMeta.last_page) {
        this.fetchResources(this.listMeta.current_page + 1)
      }
    },

    search: debounce(function() {
      this.clearResources()
      this.fetchResources()
    }, 600),

    async fetchResources(page = 1) {
      if (this.loading) return
      let params = {
        page,
        sort: this.sortOrder
          ? `${this.defaultFilter}`
          : `-${this.defaultFilter}`
      }
      if (this.filter.search) {
        params.search = this.filter.search
      }
      this.loading = true
      await this.getResources(params)
      this.loading = false
    },

    async exportList() {
      this.loading = true
      await this.exportToExcel()
        .then(response => {
          const url = URL.createObjectURL(
            new Blob([response.data], {
              type: 'application/vnd.ms-excel'
            })
          )
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'resources_list.xlsx')
          document.body.appendChild(link)
          link.click()
          this.showSnackbar('File successfully downloaded', 'success')
        })
        .catch(() => {
          this.showSnackbar('Ops! Something went wrong', 'red')
        })
      this.loading = false
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    },

    async showSearchField() {
      this.showSearch = !this.showSearch
      await this.$nextTick()
      this.$refs.searchField.$refs.input.focus()
    },

    showDetails(id) {
      this.$router.push({
        name: 'milestone.resources.details',
        params: { id }
      })
    }
  }
}
</script>
